import { createContext, useCallback, useContext, useState } from 'react';

import { Brand } from '@/models/brand';
import { useLocalStorageState } from '@/services/localStorage';
import useDebouncedState from '@/utils/useDebouncedState';

type FiltersState = {
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  brands: {
    brands: Brand[];
    setBrands: (brands: Brand[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), []);

  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const [brands, setBrandsState] = useLocalStorageState<Brand[]>(
    'settings:organization:data-catalogs:products:filters:brands',
    []
  );

  return {
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    brands: {
      brands,
      setBrands: (nextBrands: Brand[]) => {
        setBrandsState(nextBrands);
        resetPage();
      },
    },
    activeFiltersCount: brands.length,
    clearFilters: () => {
      setBrandsState([]);
      resetPage();
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
